import styled, { css } from 'styled-components';

import { ACQ, ANIM, COLORS, HOME, SPACE } from '../../../../theme/tokens';

export const Root = styled.div`
  position: relative;
`;

/** `$active`: the pill narrows what the page shows (an applied filter), in the studio's applied-filter orange. */
export const Trigger = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  color: ${(p) => (p.$active ? ACQ.orange : HOME.t50)};
  border: 2px solid ${(p) => (p.$active ? ACQ.orangeBorder : HOME.intervalBorder)};
  background: ${(p) => (p.$active ? ACQ.orangeTint : 'transparent')};
  border-radius: 100px;
  padding: 0 14px;
  cursor: pointer;
`;

/** The selected range label shown inside the pill, beside the clock icon. */
export const Value = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: inherit;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform ${ANIM.INTERACT_MS}ms ${HOME.ease};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});

  svg {
    display: block;
  }
`;

export const Menu = styled.div<{ $wide?: boolean }>`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 40;
  background: ${HOME.menuBg};
  border: 1px solid ${HOME.hairline};
  border-radius: 12px;
  padding: 5px;
  min-width: ${(p) => (p.$wide ? 'max(100%, 280px)' : '100%')};
  max-height: 60vh;
  overflow-y: auto;
  box-shadow: ${HOME.menuShadow};
`;

export const MenuItem = styled.button<{ $active: boolean; $nested?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 48px;
  text-align: left;
  border: 0;
  background: transparent;
  font-size: 12px;
  color: ${HOME.t400};
  padding: 7px 12px 7px ${(p) => (p.$nested ? '28px' : '12px')};
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${HOME.menuHoverBg};
    color: ${HOME.menuHoverText};
  }

  ${(p) =>
    p.$active &&
    css`
      color: ${HOME.t50};
      background: ${HOME.menuActiveBg};
    `}
`;

/** An option's figure, at its right: a fixed slot, so a placeholder turning into the figure moves nothing. */
export const ItemMeta = styled.span`
  margin-left: auto;
  min-width: 28px;
  display: inline-flex;
  justify-content: flex-end;
  color: ${HOME.t500};
  font-variant-numeric: tabular-nums;
`;

/** The menu's search field (the page picker's), pinned above the options while they scroll. */
export const SearchRow = styled.label`
  position: sticky;
  top: -5px; /* the menu's own inset, so it pins flush to the menu's edge */
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  margin: -5px -5px ${SPACE[4]};
  padding: ${SPACE[8]};
  background: ${HOME.menuBg};
  color: ${COLORS.textFaint};
`;

export const SearchField = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: 40px;
  padding: ${SPACE[0]} ${SPACE[12]};
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};

  &:focus-within {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  color: ${COLORS.text};
  font-size: 14px;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
  &:focus {
    outline: none;
  }
`;

export const NoMatch = styled.span`
  display: block;
  padding: ${SPACE[12]};
  font-size: 12px;
  color: ${HOME.t500};
`;

/** An option's picture, before its label. */
export const ItemIcon = styled.span`
  display: inline-flex;
  flex: none;
  margin-right: -6px; /* the row's 16px gap is for the figure; the picture sits closer to its label */
`;

/** The custom range's dates and Apply, under its row. */
export const RangeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[8]};
`;

export const RangeFields = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

export const DateField = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: ${SPACE[0]} ${SPACE[8]};
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font: inherit;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const DateArrow = styled.span`
  color: ${COLORS.textFaint};
  font-size: 13px;
`;

export const RangeFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]};
`;

/** The range's limit; it turns to a warning while the picked days break it. */
export const RangeNote = styled.span<{ $warn: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$warn ? COLORS.danger : HOME.t500)};
`;
